/**
 * useGlobeSpin Hook
 * Manages globe spinning animation
 */

import type { Map as MapboxMap } from 'mapbox-gl';
import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';
import { MAX_SPIN_ZOOM, SECONDS_PER_REVOLUTION, SLOW_SPIN_ZOOM } from '@/config/mapConfig';

export interface UseGlobeSpinReturn {
  spinGlobe: () => void;
  handleUserInteraction: () => void;
}

/**
 * Hook for managing globe spin animation
 * @param map - Mapbox map instance ref
 * @returns Spin control functions
 */
export function useGlobeSpin(map: RefObject<MapboxMap | null>): UseGlobeSpinReturn {
  const userInteracting = useRef<boolean>(false);
  const spinEnabled = useRef<boolean>(true);
  const spinInterval = useRef<NodeJS.Timeout | null>(null);

  /**
   * Start or restart globe spinning
   */
  const spinGlobe = (): void => {
    if (spinInterval.current) {
      clearInterval(spinInterval.current);
    }

    if (!spinEnabled.current || !map.current) return;

    spinInterval.current = setInterval(() => {
      if (!map.current) return;

      const zoom = map.current.getZoom();

      if (spinEnabled.current && !userInteracting.current && zoom < MAX_SPIN_ZOOM) {
        let distancePerSecond = 360 / SECONDS_PER_REVOLUTION;
        if (zoom > SLOW_SPIN_ZOOM) {
          const zoomDif = (MAX_SPIN_ZOOM - zoom) / (MAX_SPIN_ZOOM - SLOW_SPIN_ZOOM);
          distancePerSecond *= zoomDif;
        }

        const center = map.current.getCenter();
        center.lng -= distancePerSecond;
        map.current.easeTo({ center, duration: 1000, easing: (n: number) => n });
      } else {
        if (spinInterval.current) {
          clearInterval(spinInterval.current);
          spinInterval.current = null;
        }
      }
    }, 1000);
  };

  /**
   * Handle user interaction - stop spinning
   */
  const handleUserInteraction = (): void => {
    userInteracting.current = true;
    spinEnabled.current = false;
    if (spinInterval.current) {
      clearInterval(spinInterval.current);
      spinInterval.current = null;
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (spinInterval.current) {
        clearInterval(spinInterval.current);
      }
    };
  }, []);

  return {
    spinGlobe,
    handleUserInteraction,
  };
}
